import { Check } from "lucide-react";

interface Step {
  number: number;
  label: string;
  shortLabel?: string;
}

interface ProgressBarProps {
  currentStep: number;
  steps?: Step[];
}

const defaultSteps: Step[] = [
  { number: 1, label: "Current Value", shortLabel: "Current" },
  { number: 2, label: "Plan Optimization Options", shortLabel: "Options" },
  { number: 3, label: "Review Changes", shortLabel: "Review" },
  { number: 4, label: "Feedback", shortLabel: "Feedback" },
  { number: 5, label: "Confirm", shortLabel: "Confirm" },
];

export function ProgressBar({
  currentStep,
  steps = defaultSteps,
}: ProgressBarProps) {
  return (
    <div className="mb-6 md:mb-8">
      {/* Mobile Progress Bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-blue-600">
            Step {currentStep} of {steps.length}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-900">
            {Math.round((currentStep / steps.length) * 100)}% Complete
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-900 rounded-full h-2 mb-3">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
        
        {/* Current step info */}
        <div className="text-center">
          <div className="inline-flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
              {currentStep < steps.length ? (
                currentStep
              ) : (
                <Check className="h-4 w-4" />
              )}
            </div>
            <span className="text-sm font-medium text-gray-900">
              {steps[currentStep - 1]?.shortLabel || steps[currentStep - 1]?.label}
            </span>
          </div>
        </div>
      </div>

      {/* Desktop Progress Bar */}
      <div className="hidden md:block">
        <div className="relative flex justify-between">
          {/* Connecting lines */}
          <div className="absolute top-[35%] left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2 z-0 mx-10" />

          {steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center relative z-10">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center
                  ${
                    index + 1 < currentStep
                      ? "bg-primary text-primary-foreground"
                      : index + 1 === currentStep
                      ? "bg-primary text-primary-foreground border-2 border-primary outline outline-primary outline-offset-1"
                      : "bg-gray-200 dark:bg-secondary dark:text-secondary-foreground text-muted-foreground"
                  }`}
              >
                {index + 1 < currentStep ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span>{step.number}</span>
                )}
              </div>
              <span
                className={`text-xs mt-2 text-center max-w-[100px]
                ${
                  index + 1 <= currentStep
                    ? "text-blue-600 font-medium"
                    : "text-gray-500"
                }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
